import React, { Suspense, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { AuthProvider, useAuth } from './lib/auth'
import { RouterProvider } from './lib/router'
import { Shell } from './components/Shell'
import { useApi, API_BASE } from './lib/useApi'
import { ToastProvider } from './lib/toast'

const MarketingApp = React.lazy(() => import('./screens/marketing/MarketingApp').then(m => ({ default: m.MarketingApp })))
const Dashboard = React.lazy(() => import('./screens/Dashboard').then(m => ({ default: m.Dashboard })))
const JobCards = React.lazy(() => import('./screens/JobCards').then(m => ({ default: m.JobCards })))
const Quotes = React.lazy(() => import('./screens/Quotes').then(m => ({ default: m.Quotes })))
const Inbox = React.lazy(() => import('./screens/Inbox').then(m => ({ default: m.Inbox })))
const Invoices = React.lazy(() => import('./screens/Invoices').then(m => ({ default: m.Invoices })))
const Customers = React.lazy(() => import('./screens/Customers').then(m => ({ default: m.Customers })))
const Team = React.lazy(() => import('./screens/Team').then(m => ({ default: m.Team })))
const DVI = React.lazy(() => import('./screens/DVI').then(m => ({ default: m.DVI })))
const Settings = React.lazy(() => import('./screens/Settings').then(m => ({ default: m.Settings })))
const Onboarding = React.lazy(() => import('./screens/Onboarding').then(m => ({ default: m.Onboarding })))
const BackOffice = React.lazy(() => import('./screens/BackOffice').then(m => ({ default: m.BackOffice })))
const BayBoard = React.lazy(() => import('./screens/BayBoard').then(m => ({ default: m.BayBoard })))
const Vendors = React.lazy(() => import('./screens/Vendors').then(m => ({ default: m.Vendors })))
const PurchaseOrders = React.lazy(() => import('./screens/PurchaseOrders').then(m => ({ default: m.PurchaseOrders })))
const Reports = React.lazy(() => import('./screens/Reports').then(m => ({ default: m.Reports })))
const Help = React.lazy(() => import('./screens/Help').then(m => ({ default: m.Help })))
import { WelcomeTour, shouldShowTour } from './components/WelcomeTour'

type Screen = 'dashboard' | 'jobs' | 'quotes' | 'invoices' | 'inbox' | 'customers' | 'team' | 'settings' | 'bays' | 'vendors' | 'purchase-orders' | 'reports' | 'help'

interface DVIContext {
  jobCardId: string
  jobNumber: string
  vehicleReg: string
  existingInspectionId?: string
}

function AppInner() {
  const { user, isLoading, logout, getToken } = useAuth()
  const api = useApi()
  const [screen, setScreen] = useState<Screen>('dashboard')
  const isMechanic = user?.role === 'Mechanic'
  const [dvi, setDvi] = useState<DVIContext | null>(null)
  const [showNewJob, setShowNewJob] = useState(false)
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(null)
  const [jobsInitialSearch, setJobsInitialSearch] = useState<string | undefined>()
  const [inboxBadge, setInboxBadge] = useState<number | undefined>()
  const [inboxConvId, setInboxConvId] = useState<string | undefined>()
  const [purchaseOrdersInitialPoId, setPurchaseOrdersInitialPoId] = useState<string | undefined>()
  const isPlatformAdmin = user?.role === 'PlatformAdmin'
  const [showTour, setShowTour] = useState(false)

  useEffect(() => {
    if (!user) { setIsOnboarded(null); return }
    if (user.role === 'PlatformAdmin') { setIsOnboarded(true); return }
    if (user.role !== 'Owner') { setIsOnboarded(true); return }
    api.get<{ isOnboarded: boolean }>('/v1/tenants/me')
      .then(t => {
        setIsOnboarded(t.isOnboarded)
        if (t.isOnboarded) setShowTour(shouldShowTour())
      })
      .catch(() => { setIsOnboarded(true); setShowTour(shouldShowTour()) })
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || isPlatformAdmin) return
    if (isMechanic) return
    const fetchBadge = () =>
      api.get<{ open: number; pending: number; unread: number }>('/v1/whatsapp/inbox-stats')
        .then(s => setInboxBadge(s.unread > 0 ? s.unread : undefined))
        .catch(() => {})
    fetchBadge()
    const t = setInterval(fetchBadge, 30_000)
    return () => clearInterval(t)
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for inbox SSE events to update badge in real time
  useEffect(() => {
    if (!user || isPlatformAdmin || isMechanic) return
    const abort = new AbortController()
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    async function connect() {
      try {
        const token = await getToken()
        if (!token || abort.signal.aborted) return
        const res = await fetch(`${API_BASE}/v1/whatsapp/events`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abort.signal,
        })
        if (!res.ok || !res.body) throw new Error('SSE failed')
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (!abort.signal.aborted) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          let sep = buffer.indexOf('\n\n')
          while (sep >= 0) {
            const frame = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            if (frame.includes('event: inbox.updated')) {
              api.get<{ open: number; pending: number; unread: number }>('/v1/whatsapp/inbox-stats')
                .then(s => setInboxBadge(s.unread > 0 ? s.unread : undefined))
                .catch(() => {})
            }
            sep = buffer.indexOf('\n\n')
          }
        }
      } catch {
        if (!abort.signal.aborted)
          retryTimer = setTimeout(connect, 5000)
      }
    }

    connect()
    return () => {
      abort.abort()
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const payInvoiceId = window.location.pathname.match(/^\/pay\/([^/]+)$/)?.[1]
  if (payInvoiceId) return <PaymentRedirect invoiceId={payInvoiceId} />

  if (isLoading || (user && isOnboarded === null)) {
    return <LoadingScreen />
  }

  if (!user) return <RouterProvider><Suspense fallback={<LoadingScreen />}><MarketingApp /></Suspense></RouterProvider>

  if (isPlatformAdmin) return <Suspense fallback={<LoadingScreen />}><BackOffice /></Suspense>

  if (!isOnboarded) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Onboarding onComplete={() => { setIsOnboarded(true); setShowTour(true) }} />
      </Suspense>
    )
  }

  // DVI is a full-screen overlay over the shell
  if (dvi) {
    return (
      <div style={{ height: '100vh', overflow: 'hidden' }}>
        <Suspense fallback={<LoadingScreen />}>
          <DVI
            jobCardId={dvi.jobCardId}
            jobNumber={dvi.jobNumber}
            vehicleReg={dvi.vehicleReg}
            existingInspectionId={dvi.existingInspectionId}
            onClose={() => setDvi(null)}
          />
        </Suspense>
      </div>
    )
  }

  function openNewJob() { setJobsInitialSearch(undefined); setScreen('jobs'); setShowNewJob(true) }

  function startTour() {
    localStorage.removeItem('axxiz_tour_v1')
    setShowTour(true)
  }

  const MECHANIC_ALLOWED: Screen[] = ['dashboard', 'jobs', 'bays', 'purchase-orders', 'help', 'settings']

  function navTo(s: Screen) {
    if (isMechanic && !MECHANIC_ALLOWED.includes(s)) return
    if (s !== 'jobs') setJobsInitialSearch(undefined)
    if (s !== 'purchase-orders') setPurchaseOrdersInitialPoId(undefined)
    setScreen(s)
  }

  function openPurchaseOrder(poId: string) {
    setJobsInitialSearch(undefined)
    setPurchaseOrdersInitialPoId(poId)
    setScreen('purchase-orders')
  }

  function openJobsForCustomer(customerName: string) {
    setJobsInitialSearch(customerName)
    setScreen('jobs')
  }

  async function openWhatsAppForCustomer(customerId: string) {
    try {
      const conv = await api.post<{ id: string }>('/v1/whatsapp/conversations', { customerId })
      setInboxConvId(conv.id)
    } catch {
      setInboxConvId(undefined)
    }
    setScreen('inbox')
  }

  let view: React.ReactNode
  switch (screen) {
    case 'dashboard': view = <Dashboard onNewJob={openNewJob} />; break
    case 'jobs':
      view = (
        <JobCards
          key={jobsInitialSearch ?? '__default__'}
          initialSearch={jobsInitialSearch}
          openNewModal={showNewJob}
          onNewModalClose={() => setShowNewJob(false)}
          onViewDVI={job => setDvi({
            jobCardId: job.id,
            jobNumber: job.jobNumber,
            vehicleReg: job.vehicle.registration,
            existingInspectionId: job.inspection?.id ?? undefined,
          })}
        />
      )
      break
    case 'quotes':    view = <Quotes onNav={s => navTo(s as Screen)} onOpenPurchaseOrder={openPurchaseOrder} />;    break
    case 'inbox':     view = <Inbox key={inboxConvId ?? '__inbox__'} initialConversationId={inboxConvId} onOpenJobsForCustomer={openJobsForCustomer} />;     break
    case 'invoices':  view = <Invoices />;  break
    case 'customers': view = <Customers onMessage={openWhatsAppForCustomer} />; break
    case 'team':             view = <Team />;           break
    case 'settings':         view = <Settings />;        break
    case 'bays':             view = <BayBoard />;        break
    case 'vendors':          view = <Vendors />;         break
    case 'purchase-orders':  view = <PurchaseOrders initialOpenPoId={purchaseOrdersInitialPoId} onInitialOpenHandled={() => setPurchaseOrdersInitialPoId(undefined)} />;  break
    case 'reports':          view = <Reports />;          break
    case 'help':             view = <Help onStartTour={!isMechanic ? startTour : undefined} />; break
  }

  return (
    <>
      <Shell active={screen} onNav={navTo} onNewJob={isMechanic ? undefined : openNewJob} onSettings={() => { navTo('settings') }} onLogout={logout} onSearch={isMechanic ? undefined : openJobsForCustomer} inboxBadge={isMechanic ? undefined : inboxBadge}>
        <Suspense fallback={<LoadingScreen />}>{view}</Suspense>
      </Shell>
      {showTour && !isMechanic && (
        <WelcomeTour
          onFinish={() => setShowTour(false)}
          onCreateJob={() => { setShowTour(false); openNewJob() }}
        />
      )}
    </>
  )
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-canvas, #F8F9FA)', color: 'var(--fg-2, #6B7280)', fontSize: 14,
    }}>
      Loading…
    </div>
  )
}

function PaymentRedirect({ invoiceId }: { invoiceId: string }) {
  const [error, setError] = useState(false)
  useEffect(() => {
    fetch(`${API_BASE}/v1/pay/${encodeURIComponent(invoiceId)}`)
      .then(r => r.ok ? r.json() as Promise<{ url: string }> : Promise.reject())
      .then(data => { window.location.href = data.url })
      .catch(() => setError(true))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 12,
      background: 'var(--bg-canvas, #F8F9FA)', color: 'var(--fg-2, #6B7280)', fontSize: 14,
    }}>
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--fg-1, #111827)' }}>Payment link not available</p>
      <p style={{ margin: 0 }}>Please contact the workshop directly.</p>
    </div>
  )
  return <LoadingScreen />
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
      <AuthProvider>
        <ToastProvider>
          <AppInner />
        </ToastProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

function ErrorScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 12,
      background: 'var(--bg-canvas, #F8F9FA)', color: 'var(--fg-2, #6B7280)', fontSize: 14,
    }}>
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--fg-1, #111827)' }}>Something went wrong</p>
      <p style={{ margin: 0 }}>The error has been reported. Refresh to try again.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ marginTop: 8, padding: '8px 16px', border: '1px solid var(--border-subtle)', borderRadius: 6, cursor: 'pointer', background: 'white' }}
      >
        Refresh
      </button>
    </div>
  )
}
